"use client";
import Image from "next/image";
import { useRef, useEffect, useState } from "react";
import {
  MdPhoneInTalk,
  MdSearch,
  MdAttachMoney,
  MdRoofing,
  MdSentimentVerySatisfied,
} from "react-icons/md";
import {
  FaWrench,
  FaCheck,
  FaUsers,
  FaSeedling,
} from "react-icons/fa";

const trustItems = [
  {
    color: "bg-[#DC3332]",
    icon: <FaWrench className="text-white text-xl" />,
    title: "Excellence in Every Project",
    description:
      "Craftsmanship that lasts. Powered by trusted brands like Owens Corning.",
  },
  {
    color: "bg-[#DC3332]",
    icon: <FaCheck className="text-white text-xl" />,
    title: "Trust That Lasts",
    description:
      "We treat every customer like family \u2014 honesty and respect first.",
  },
  {
    color: "bg-[#DC3332]",
    icon: <FaUsers className="text-white text-xl" />,
    title: "Making a Difference",
    description:
      "Beyond roofing: community care, first responder discounts, and giving back.",
  },
  {
    color: "bg-[#DC3332]",
    icon: <FaSeedling className="text-white text-xl" />,
    title: "Growth & Opportunity",
    description:
      "Innovation and respect drive us \u2014 for our team and for your home.",
  },
];

const defaultSteps = [
  { icon: <MdPhoneInTalk className="text-2xl text-[#DC3332]" />, label: "Call Us!", desc: "Schedule your free roof inspection." },
  { icon: <MdSearch className="text-2xl text-[#DC3332]" />, label: "We Inspect", desc: "Certified experts check every detail." },
  { icon: <MdAttachMoney className="text-2xl text-[#DC3332]" />, label: "Get Your Estimate", desc: "Clear, fair, and transparent pricing." },
  { icon: <MdRoofing className="text-2xl text-[#DC3332]" />, label: "Roof Installation", desc: "Skilled pros build it to last." },
  { icon: <MdSentimentVerySatisfied className="text-2xl text-[#DC3332]" />, label: "Happy Roof, Happy Home", desc: "Sit back & enjoy peace of mind." },
];

function StepCards({ steps }: { steps: { icon: React.ReactNode; label: string; desc: string }[] }) {
  // Track in-view state ourselves (no framer useInView). We can't use the
  // disconnect-on-first-intersect helper here because the bounce loop has
  // to pause when the user scrolls away and resume when they come back.
  const ref = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);
  const [cycle, setCycle] = useState(0);

  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) setIsInView(e.isIntersecting);
      },
      { threshold: 0.4 },
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  // Re-trigger the bounce sequence every 6s while in view. Bumping `cycle`
  // changes each card's React key, which remounts the element and restarts
  // its CSS keyframe animation — same effect as framer's animate prop loop.
  useEffect(() => {
    if (!isInView) return;
    const interval = setInterval(() => setCycle((c) => c + 1), 6000);
    return () => clearInterval(interval);
  }, [isInView]);

  return (
    <div ref={ref} className="hidden md:block lg:pr-[280px]">
      {/* 5 steps in one row; 6 steps as two rows of 3 (no orphan card). */}
      <div className={`grid gap-5 ${steps.length > 5 ? "grid-cols-3" : "grid-cols-5"}`}>
        {steps.map((step, index) => (
          <div
            key={`${index}-${cycle}`}
            className="group relative bg-[#F3F3F3] rounded-2xl p-5 pt-8 text-center ring-1 ring-black/5 hover:shadow-xl hover:ring-[#DC3332]/20 transition-all duration-300 animate-step-bounce"
            style={{ animationDelay: `${index * 400}ms` }}
          >
            {/* Step number */}
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-7 h-7 rounded-full bg-[#DC3332] text-white text-xs font-bold font-['Outfit'] flex items-center justify-center shadow-md">
              {index + 1}
            </div>
            {/* Icon */}
            <div className="w-14 h-14 rounded-xl bg-white flex items-center justify-center mx-auto mb-4 shadow-sm group-hover:shadow-md transition-shadow">
              <div className="text-2xl text-[#DC3332]">{step.icon}</div>
            </div>
            <h3 className="font-['Outfit'] text-sm font-bold text-[#1C1C1C] mb-2">
              {step.label}
            </h3>
            <p className="text-xs text-[#6B7280] leading-relaxed">{step.desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function TrustAndPayments() {
  return (
    <>
      {/* Section 1: Why Texas Trusts Ruff Roofing */}
      <section className="-mt-6 pt-0 pb-10 px-6 lg:px-12 bg-white">
        <div className="max-w-[1600px] mx-auto">
          {/* Header */}
          <div className="mb-10">
            <h2 className="font-['Outfit'] text-4xl sm:text-5xl md:text-7xl lg:text-[90px] font-extrabold text-[#DC3332] leading-[0.95] text-right">
              Why Choose Us?
            </h2>
            <p className="mt-0.5 text-[#54595F] font-semibold text-[11px] sm:text-base md:text-lg lg:text-xl whitespace-nowrap">
              <span className="sm:hidden">It&apos;s more than shingles—it&apos;s about trust, care &amp; lasting protection.</span>
              <span className="hidden sm:inline">It&apos;s more than shingles — it&apos;s about trust, care, and lasting protection.</span>
            </p>
          </div>

          {/* Bento grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5 auto-rows-auto sm:auto-rows-[200px] lg:auto-rows-[220px]">
            {/* Team image — tall left block */}
            <div className="relative rounded-2xl overflow-hidden col-span-1 sm:col-span-2 row-span-1 sm:row-span-2 min-h-[220px] sm:min-h-0">
              <Image
                src="/images/DJI_20000207160226_0227_D.jpg"
                alt="Ruff Roofing team on site"
                fill
                style={{ objectFit: "cover", objectPosition: "center 70%" }}
                sizes="(max-width: 1024px) 100vw, 50vw"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
            </div>

            {/* 4 Cards */}
            {trustItems.map((item) => (
              <div
                key={item.title}
                className="group relative bg-white rounded-2xl p-5 sm:p-6 shadow-md ring-1 ring-black/5 hover:shadow-xl hover:-translate-y-1 transition-all duration-500 overflow-hidden flex flex-col justify-between min-h-[180px] sm:min-h-0"
              >
                <div>
                  <div
                    className={`${item.color} w-11 h-11 rounded-xl flex items-center justify-center mb-4`}
                  >
                    {item.icon}
                  </div>
                  <h3 className="text-[#1C1C1C] font-bold text-base lg:text-lg font-['Outfit'] leading-snug">
                    {item.title}
                  </h3>
                </div>
                <p className="text-[#6B7280] text-xs lg:text-sm leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sections 2 & 3: Sky background — Payments + Warranty */}
      <div
        className="relative bg-cover bg-center"
        style={{
          backgroundImage:
            "linear-gradient(rgba(0,0,0,0.2), rgba(0,0,0,0.2)), url('/images/optimized/sky.webp')",
        }}
      >
        {/* Top fade — matches the section above (white) */}
        <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-white to-transparent z-[1]" />
        {/* Bottom fade — matches the section below (white) */}
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-white to-transparent z-[1]" />

        {/* Section 2: Stress-Free Roofing Payments */}
        <div className="relative z-10 max-w-6xl mx-auto px-4 text-center pt-16 pb-24">
          <h2 className="font-['Outfit'] text-2xl sm:text-3xl md:text-[44px] font-extrabold text-white leading-tight">
            Stress-Free Roofing Payments
          </h2>
          <p className="mt-4 text-white/85 text-base md:text-lg font-semibold leading-relaxed max-w-xl mx-auto">
            Affordable plans starting at 0% APR — no matter your credit.
          </p>
          <a
            href="/financing"
            className="inline-block mt-8 bg-[#DC3332] text-white px-10 py-4 rounded-full font-semibold text-sm hover:bg-red-700 transition-colors"
          >
            See if you qualify
          </a>
        </div>

        {/* Section 3: Warranties That Last a Lifetime */}
        <div className="relative z-10 pb-24">
          {/* Roof image on left — full bleed to viewport edge */}
          <div className="absolute left-0 -top-48 bottom-0 w-[60vw] hidden lg:block pointer-events-none mix-blend-multiply">
            <Image
              src="/images/roof-corner-v2.png"
              alt="Roof detail"
              fill
              style={{ objectFit: "contain", objectPosition: "left bottom" }}
              sizes="50vw"
            />
          </div>

          {/* Right — Text */}
          <div className="max-w-6xl mx-auto flex flex-col lg:flex-row items-center gap-10 px-4">
          <div className="hidden lg:block w-1/2" />
          <div className="w-full lg:w-1/2">
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl md:text-[56px] font-extrabold text-white leading-[1.1]">
              Warranties That Last a Lifetime
            </h2>
            <p className="mt-5 text-white/80 text-lg md:text-xl leading-relaxed">
              30–50 years of coverage on labor and materials — because we stand by our work.
            </p>
          </div>
          </div>
        </div>
      </div>

      <HowItWorks />
    </>
  );
}

/** "How It Works" 5-step promise — also rendered on its own on the homepage. */
export function HowItWorks({
  title,
  subtitle = "Simple. Smooth. Stress-free from start to finish.",
  steps: customSteps,
  teamImage = { src: "/images/optimized/team-thumbsup-2.webp", alt: "Ruff Roofing team" },
}: {
  /** Custom (longer) heading — rendered at a normal H2 size instead of the 90px display style. */
  title?: string;
  subtitle?: string;
  /** Step text overrides; icons keep the default 5-step order. */
  steps?: { label: string; desc: string }[];
  /** Team photo beside the steps; city pages pass a location-named copy. */
  teamImage?: { src: string; alt: string };
} = {}) {
  const steps = customSteps
    ? customSteps.map((s, i) => ({
        ...s,
        // A 6th step (e.g. final review) gets a check icon instead of repeating the phone.
        icon: i < defaultSteps.length ? defaultSteps[i].icon : <FaCheck className="text-2xl text-[#DC3332]" />,
      }))
    : defaultSteps;
  return (
    <>
      {/* Section 4: Our 5-Step Promise */}
      <section className="pt-12 pb-8 px-6 lg:px-12 bg-white relative overflow-hidden">
        <div className="max-w-[1600px] mx-auto relative z-10 lg:pr-[80px] xl:pr-[100px] 2xl:pr-[120px]">
          {/* Header — same style as "We Do It All" and "Why Choose Us" */}
          <div className="mb-12">
            {title ? (
              <>
                <h2 className="font-['Outfit'] text-3xl sm:text-4xl md:text-5xl font-extrabold text-[#1C1C1C] leading-[1.1] max-w-4xl">
                  {title}
                </h2>
                {subtitle && <p className="mt-3 text-[#54595F] font-semibold text-base md:text-lg">{subtitle}</p>}
              </>
            ) : (
              <>
                <h2 className="font-['Outfit'] text-4xl sm:text-5xl md:text-7xl lg:text-[90px] font-extrabold text-[#DC3332] leading-[0.95] -ml-[4px]">
                  How It Works.
                </h2>
                <p className="mt-0.5 text-[#54595F] font-semibold text-[11px] sm:text-base md:text-lg lg:text-xl text-right lg:text-left whitespace-nowrap">
                  {subtitle}
                </p>
              </>
            )}
          </div>

          {/* Desktop: 5 step cards */}
          <StepCards steps={steps} />

          {/* Mobile: vertical timeline */}
          <div className="relative flex flex-col gap-0 md:hidden">
            <div className="absolute left-[23px] top-10 bottom-10 w-[3px] rounded-full z-0 bg-gradient-to-b from-[#DC3332] via-[#DC3332]/40 to-[#DC3332]" />
            {steps.map((step, index) => (
              <div
                key={index}
                className="relative z-10 flex items-start gap-5 py-4"
              >
                <div className="w-12 h-12 rounded-full bg-[#DC3332] flex items-center justify-center shrink-0 text-white text-sm font-bold font-['Outfit'] shadow-lg shadow-[#DC3332]/20">
                  {index + 1}
                </div>
                <div className="bg-[#F3F3F3] rounded-xl p-4 flex-1 ring-1 ring-black/5">
                  <div className="flex items-center gap-3 mb-1">
                    <div className="text-[#DC3332]">{step.icon}</div>
                    <h3 className="font-['Outfit'] text-base font-bold text-[#1C1C1C]">
                      {step.label}
                    </h3>
                  </div>
                  <p className="text-sm text-[#6B7280] leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Man image — right side */}
        <div className="hidden lg:block absolute -right-8 xl:-right-4 2xl:right-0 bottom-[6px] z-20 pointer-events-none">
          <Image
            src={teamImage.src}
            alt={teamImage.alt}
            width={414}
            height={602}
            className="w-[500px] xl:w-[560px] 2xl:w-[620px] h-auto object-contain object-bottom"
          />
        </div>

      </section>
    </>
  );
}
